import { PlaceOutlined } from '@mui/icons-material';
import { Box, Tooltip } from '@mui/material';
import { FC, KeyboardEvent, MouseEvent } from 'react';

import { SameLocationInfo, SameLocationTarget } from './sameLocationVM';

/**
 * ツールチップに並べる訪問の上限。全部並べると 1 地点に 10 台以上寄る日で
 * ツールチップが画面を覆うため、溢れは件数だけ伝える。
 *
 * 切るのは**表示だけ**で、バッジのクリックは溢れた分も選ぶ (log_web_issues#323)。
 * バッジの件数が n なら n 台選ばれるのが読み手の期待。
 */
export const SAME_LOCATION_TOOLTIP_LIMIT = 6;

export type SameLocationTagProps = {
  info: SameLocationInfo;
  /**
   * 対象の回転行を選ぶ。渡すのは回転行の rowId 全部で、呼び出し側が
   * 「まとめて選択して車両リストを先頭の行までスクロール」する。
   * 未指定なら行をボタンにしない（押しても何も起きない操作を誘わない）。
   *
   * 行を押したときは 1 件、バッジを押したときは `targets` 全件（log_web_issues#323）。
   */
  onSelectVehicles?: (rowIds: readonly string[]) => void;
};

/**
 * 1 台ぶんの行。1 行目に車両名と割当済み側の地点名、2 行目にドライバー・運送会社・回転。
 *
 * Why 2 行: #318 では 1 行に収めるためドライバー名と会社名を落としていたが、
 * 「どの会社の誰に割ついているのか」が読めず運用で要望が出た (log_web_issues#323)。
 * 縦に 4 行積むと 1 台の切れ目が読めなくなるので、共通配送先パネル
 * (`detailPanel/SharedDestinationPanel`) と同じ「太字の名前＋muted の補足 1 行」に揃える。
 */
const TargetRow: FC<{
  target: SameLocationTarget;
  onSelect?: (rowIds: readonly string[]) => void;
}> = ({ target, onSelect }) => {
  // 会社名は自社車両で空文字になり、ドライバーも未設定の勤務がある。
  // 空のまま繋ぐと「矢野 利秀 ・  ・ 1回転目」と区切りだけが残るので、
  // 空の要素は区切りごと落とす（車両リスト 1 段目 VehicleInfoView と同じ扱い）
  const subText = [target.driverName, target.companyName, `${target.rotationNumber}回転目`]
    .filter((part) => !!part)
    .join(' ・ ');

  return (
    <Box
      // 押せない行（呼び出し側が選択を渡さない画面）は disabled ではなく aria-disabled に
      // する。要素ごと読み取り不能にしたいわけではないため。
      // Why not tabIndex={-1} も足す: この行はキーボードから到達できない
      //（ツールチップの中身は portal に出るのでバッジからの Tab は入らない）ので、
      // 足しても効果が無い。キーボードからの選択はバッジ側で行う (log_web_issues#323)
      component="button"
      type="button"
      {...(onSelect ? {} : { 'aria-disabled': true })}
      // 伝播停止はここに書かない。境界は portal の最も外側 (slotProps.popper) に
      // 1 か所だけ置く。2 か所あるとどちらが効いているのか読めない
      onClick={() => onSelect?.([target.rowId])}
      sx={{
        display: 'block',
        width: '100%',
        // 1 台が 2 行になったので行間を #318 の 2px から広げる。詰めると
        // どこまでが 1 台なのか読めない（1 行だった頃と同じ問題が起きる）。
        // 2 行目を 1 行目と同じ字送りにした分（指摘 2026-09-07）、台と台の切れ目は
        // 行内の間隔より広くないと読めないのでここで差を付ける
        mt: 0.75,
        px: 0.5,
        mx: -0.5,
        py: '1px',
        borderRadius: '4px',
        textAlign: 'left',
        // button にすると UA 既定の枠と背景が付くので消す。色は Tooltip から継承させる
        border: 'none',
        bgcolor: 'transparent',
        color: 'inherit',
        font: 'inherit',
        cursor: onSelect ? 'pointer' : 'default',
        '&:hover': onSelect ? { bgcolor: 'hsl(var(--background) / 0.16)' } : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
        <Box component="span" sx={{ flexShrink: 0, fontWeight: 700, whiteSpace: 'nowrap' }}>
          {target.vehicleNumber}
        </Box>
        {/* 省略記号で切らない。地点名は「どこが重複しているか」そのものなので、
            長ければ折り返す。車両名が nowrap なので折り返しても行の頭は揃う */}
        <Box component="span" sx={{ minWidth: 0, flex: 1, opacity: 0.85, wordBreak: 'break-word' }}>
          {target.storeName}
        </Box>
      </Box>
      {/* 字面は 1 行目と同じ。サイズを落とさないのは、初版の fontSize 10 が
          「車両名と違うフォントに見えて読みにくい」と読まれたため（指摘 2026-09-07）。
          1 台の切れ目は車両名の太字だけで示し、2 行目は濃さを 1 行目の地点名 (0.85) に
          合わせて本文と同じ扱いにする。

          Why not 色を `muted-foreground` にする（共通配送先パネルはそうしている）:
          ツールチップの背景が `--foreground`（既定テーマではほぼ黒）なので、
          `--muted-foreground`（L=46.9%）を置くと暗い背景に暗い文字で読めなくなる。
          このツールチップは「ほか N 件」も opacity で落としており、色は Tooltip から
          継承したまま opacity で弱めるのがここでの muted 表現 */}
      <Box component="span" sx={{ display: 'block', opacity: 0.85 }}>
        {subText}
      </Box>
    </Box>
  );
};

/**
 * 未割当案件の「同一地点がすでに他車両へ割当済み」タグ (log_web_issues#318)。
 *
 * 見た目はサンプル (logposeTech/route-planner の UnassignedPanel) 準拠。
 * 配色を配車カードの警告バッジ「他車両での取扱あり」と同じ primary 系に揃えるのは、
 * 「同一地点が他車両にもある」という同じ意味の表示だと読み手に分からせるため。
 */
export const SameLocationTag: FC<SameLocationTagProps> = ({ info, onSelectVehicles }) => {
  const { targets } = info;
  if (targets.length === 0) return null;

  const shown = targets.slice(0, SAME_LOCATION_TOOLTIP_LIMIT);
  const rest = targets.length - shown.length;

  return (
    <Tooltip
      placement="left"
      enterDelay={120}
      // describeChild を付けないと Tooltip がバッジに aria-labelledby を張り、
      // バッジの読み上げ名がツールチップ全文に置き換わる（UnassignedCardContent の
      // 「分割を戻す」Tooltip と同じ理由）。バッジ自身の文言を名前として残す
      describeChild
      slotProps={{
        /*
         * 伝播を止める境界はここ 1 か所だけ。ツールチップの中身は portal に出るが
         * React ツリー上は Tooltip の子なので、synthetic event は未割当カードまで
         * 伝播する。漏れると未割当案件の選択が切り替わり、カード型配車では
         * pointerDown から 3px 動かすだけで D&D が始まる。
         *
         * Why 行ごとではなく popper: 行に付けると見出し・「ほか N 件」・パネルの余白が
         * 漏れる（実際にそうなっていた）。popper は portal の最も外側で、
         * transition と tooltip 箱をまとめて包むのでこれ 1 つで中身すべてを守れる
         * （インストール済みの Tooltip.js の PopperSlot → TransitionSlot →
         * TooltipSlot の入れ子と、role="tooltip" が BasePopper.js:193 で
         * popper 側に付くことを確認）。
         *
         * MUI 自身が popper へ渡すのは interactiveWrapperListeners の
         * onMouseOver / onMouseLeave / onFocus だけなので、ここで上書きしない。
         *
         * preventDefault はしない。すると行ボタン自身の click が出なくなる。
         * keyDown も止めるのは、親カードが Enter/Space を無条件に preventDefault()
         * ＋案件選択へ変換するため。
         */
        popper: {
          onClick: (e: MouseEvent) => { e.stopPropagation(); },
          onPointerDown: (e: MouseEvent) => { e.stopPropagation(); },
          onKeyDown: (e: KeyboardEvent) => { e.stopPropagation(); },
        },
        tooltip: {
          sx: { maxWidth: 420, bgcolor: 'hsl(var(--foreground))' },
        },
      }}
      title={(
        <Box sx={{ fontSize: 12, lineHeight: 1.6, minWidth: 240 }}>
          <Box sx={{ fontWeight: 700, mb: 0.5 }}>同一地点がすでに割当済みです</Box>
          {shown.map((t) => (
            // 同一行が同一座標の別名称カードを持つので、行だけでは key にならない
            <TargetRow key={`${t.rowId}-${t.storeName}`} target={t} onSelect={onSelectVehicles} />
          ))}
          {rest > 0 && <Box sx={{ mt: 0.5, opacity: 0.6 }}>{`ほか ${rest} 件`}</Box>}
        </Box>
      )}
    >
      <Box
        // button にするのはフォーカスできるようにするため。span だとキーボードだけでは
        // ツールチップを開けず、どの車両に割ついているか読む手段が無くなる。
        // 同じカードに並ぶ未割当理由バッジ (NotAllocReasonPresenter) も同じ形。
        // Enter / Space は素の button のネイティブ起動が click に変わるので、
        // キーボードからも下の onClick（＝ n 台の選択）が走る (log_web_issues#323)
        component="button"
        type="button"
        // 未割当カードは D&D の掴み手で、クリック・Enter/Space で選択も切り替わる。
        // タグを触ってそれが起きると邪魔なので、どのイベントも親へ渡さない。
        // stopPropagation だけで preventDefault はしない（すると Enter / Space の
        // ネイティブ起動が潰れ、バッジ自身の click が一切出なくなる）
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          // 選ぶ単位は行なので、同一行の別名称カード 2 枚を 2 回渡さない
          onSelectVehicles?.([...new Set(targets.map((t) => t.rowId))]);
        }}
        onPointerDown={(e: MouseEvent) => e.stopPropagation()}
        onKeyDown={(e: KeyboardEvent) => e.stopPropagation()}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 0.5,
          px: 0.75,
          py: '2px',
          borderRadius: '3px',
          // button の UA 既定フォントを継承へ戻したうえで大きさを指定する
          fontFamily: 'inherit',
          fontSize: 10,
          fontWeight: 600,
          lineHeight: 1.2,
          // 選択を渡さない画面では押しても何も起きないので、pointer にしない
          cursor: onSelectVehicles ? 'pointer' : 'help',
          bgcolor: 'hsl(var(--primary) / 0.12)',
          color: 'hsl(var(--primary))',
          border: '1px solid hsl(var(--primary) / 0.35)',
        }}
      >
        <PlaceOutlined sx={{ fontSize: 12 }} />
        {`同一地点で割当済 (${targets.length})`}
      </Box>
    </Tooltip>
  );
};

SameLocationTag.displayName = 'SameLocationTag';
